import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ApiError } from '../lib/api.js';
import { fetchPushInfo, removePushSubscription, savePushSubscription } from '../lib/notifications-api.js';
import { t } from '../lib/i18n.js';

/**
 * "Push on this device" (spec §13.6).
 *
 * Push is per device, not per person: a phone and a laptop are switched on
 * separately, and the browser asks the person's permission each time. So this
 * control reports what THIS device is doing, and says plainly why it cannot,
 * when it cannot — the server has no keys, the browser has no push support, or
 * the person blocked notifications for this site.
 */

type DeviceState =
  | { kind: 'checking' }
  | { kind: 'unsupported'; reason: string }
  | { kind: 'blocked' }
  | { kind: 'off' }
  | { kind: 'on'; endpoint: string };

/** The server's key, as the bytes `pushManager.subscribe` wants. */
function keyBytes(base64Url: string): ArrayBuffer {
  const padded = `${base64Url}${'='.repeat((4 - (base64Url.length % 4)) % 4)}`.replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(padded);
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let index = 0; index < raw.length; index += 1) bytes[index] = raw.charCodeAt(index);
  return bytes.buffer;
}

function pushSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    'Notification' in window
  );
}

async function currentRegistration(): Promise<ServiceWorkerRegistration | undefined> {
  return navigator.serviceWorker.getRegistration();
}

export function PushDeviceControl() {
  const info = useQuery({ queryKey: ['notifications', 'push'], queryFn: fetchPushInfo, retry: false });
  const [device, setDevice] = useState<DeviceState>({ kind: 'checking' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!pushSupported()) {
        if (!cancelled) {
          setDevice({
            kind: 'unsupported',
            reason:
              'This browser cannot receive push notifications. On an iPhone or iPad, add QMS360 to the Home Screen and open it from there.',
          });
        }
        return;
      }
      if (Notification.permission === 'denied') {
        if (!cancelled) setDevice({ kind: 'blocked' });
        return;
      }
      const registration = await currentRegistration();
      if (!registration) {
        if (!cancelled) {
          setDevice({ kind: 'unsupported', reason: 'Push needs the installed app shell, which is not running in this window.' });
        }
        return;
      }
      const subscription = await registration.pushManager.getSubscription();
      if (!cancelled) setDevice(subscription ? { kind: 'on', endpoint: subscription.endpoint } : { kind: 'off' });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const fail = (caught: unknown, fallback: string) =>
    setError(caught instanceof ApiError ? caught.message : caught instanceof Error ? caught.message : fallback);

  const turnOn = async () => {
    if (!info.data?.publicKey) return;
    setBusy(true);
    setError(null);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setDevice(permission === 'denied' ? { kind: 'blocked' } : { kind: 'off' });
        return;
      }
      const registration = await currentRegistration();
      if (!registration) throw new Error(t("The app shell is not running in this window."));
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: keyBytes(info.data.publicKey),
      });
      const json = subscription.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
      await savePushSubscription({ endpoint: json.endpoint, keys: json.keys });
      setDevice({ kind: 'on', endpoint: json.endpoint });
    } catch (caught) {
      fail(caught, t("Push could not be switched on for this device."));
    } finally {
      setBusy(false);
    }
  };

  const turnOff = async () => {
    if (device.kind !== 'on') return;
    setBusy(true);
    setError(null);
    try {
      const registration = await currentRegistration();
      const subscription = await registration?.pushManager.getSubscription();
      await subscription?.unsubscribe();
      await removePushSubscription(device.endpoint).catch(() => undefined);
      setDevice({ kind: 'off' });
    } catch (caught) {
      fail(caught, t("Push could not be switched off for this device."));
    } finally {
      setBusy(false);
    }
  };

  let body: React.ReactNode;
  if (info.data && !info.data.available) {
    body = <span className="text-content-secondary">{info.data.reason ?? t("Push is not set up on this server yet.")}</span>;
  } else if (device.kind === 'checking' || info.isLoading) {
    body = <span className="text-content-secondary">{t("Checking this device…")}</span>;
  } else if (device.kind === 'unsupported') {
    body = <span className="text-content-secondary">{device.reason}</span>;
  } else if (device.kind === 'blocked') {
    body = (
      <span className="text-content-secondary">
        {t("Notifications are blocked for this site in your browser’s settings. Allow them there, then come back.")}
      </span>
    );
  } else if (device.kind === 'on') {
    body = (
      <>
        <span>{t("On for this device.")}</span>
        <button type="button" disabled={busy} onClick={() => void turnOff()} className="rounded border border-border px-3 py-1 disabled:opacity-60">
          {t("Turn off here")}
        </button>
      </>
    );
  } else {
    body = (
      <button
        type="button"
        disabled={busy || !info.data?.publicKey}
        onClick={() => void turnOn()}
        className="rounded bg-ag-purple px-3 py-1 text-white disabled:opacity-60"
      >
        {t("Turn on push for this device")}
      </button>
    );
  }

  return (
    <div className="mt-4 border-t border-border pt-3 text-sm" data-testid="push-device">
      <p className="m-0 font-medium">{t("Push on this device")}</p>
      <div className="mt-2 flex flex-wrap items-center gap-3">{body}</div>
      {error && (
        <p role="alert" className="mb-0 mt-2" style={{ color: 'var(--tone-critical)' }}>
          {error}
        </p>
      )}
    </div>
  );
}
